import React, { useState } from "react";
import { Loader2, Save, Plus, Trash2, ArrowUp, ArrowDown, Upload, Image as ImageIcon, Video } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const inputCls = "w-full mt-1 px-3 py-2 rounded-lg nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-semibold";
const areaCls = "w-full mt-1 px-3 py-2 rounded-lg nb-border bg-[#FFF9F0] focus:outline-none focus:bg-white font-medium text-sm min-h-[90px]";

// Read/write one language side of a bilingual {en, fr} content field (tolerates legacy strings).
const getL = (v, lang) => (v && typeof v === "object" ? v[lang] || "" : lang === "en" ? v || "" : "");
const setL = (v, lang, val) => {
  const base = v && typeof v === "object" ? { en: v.en || "", fr: v.fr || "" } : { en: typeof v === "string" ? v : "", fr: "" };
  return { ...base, [lang]: val };
};
const EMPTY_BI = { en: "", fr: "" };

function Section({ title, desc, children }) {
  return (
    <div className="bg-white nb-border nb-shadow rounded-2xl p-6">
      <h3 className="font-head font-extrabold text-xl mb-1">{title}</h3>
      {desc && <p className="text-sm text-nbblack/50 mb-4">{desc}</p>}
      {children}
    </div>
  );
}

// Editor for one material's description (heading, body markdown, media list).
const LAYOUTS = [
  { v: "text", label: "Text only" },
  { v: "image", label: "Image (full / wide)" },
  { v: "image_left", label: "Image left · text right" },
  { v: "image_right", label: "Image right · text left" },
  { v: "video", label: "Video (full / wide)" },
  { v: "video_left", label: "Video left · text right" },
  { v: "video_right", label: "Video right · text left" },
];

// Row-based description builder: text, image/video (wide or centered),
// and split image/text rows (image left/right).
function MaterialEditor({ mat, desc, update, lang }) {
  const [uploadingIdx, setUploadingIdx] = useState(-1);
  const rows = desc.rows || [];

  const setRows = (next) => update({ ...desc, rows: next });
  const addRow = (layout) => setRows([...rows, { layout, text_md: { ...EMPTY_BI }, media_url: "", caption: { ...EMPTY_BI }, width: "wide" }]);
  const updRow = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const delRow = (i) => setRows(rows.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[i], next[j]] = [next[j], next[i]];
    setRows(next);
  };

  const upload = async (i, file) => {
    if (!file) return;
    setUploadingIdx(i);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/content/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updRow(i, { media_url: data.url });
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingIdx(-1);
    }
  };

  return (
    <div className="border-b border-nbblack/10 pb-6 mb-6 last:border-0 last:mb-0 last:pb-0" data-testid={`content-material-${mat.key}`}>
      <h4 className="font-head font-extrabold text-lg mb-3">{mat.name}</h4>
      <label className="text-xs font-bold text-nbblack/60">Heading</label>
      <input value={getL(desc.heading, lang)} onChange={(e) => update({ ...desc, heading: setL(desc.heading, lang, e.target.value) })} className={inputCls} data-testid={`content-heading-${mat.key}`} />

      <div className="mt-4 space-y-3">
        {rows.map((r, i) => {
          const layout = r.layout || "text";
          const kind = layout.startsWith("video") ? "video" : "image";
          const hasText = layout === "text" || layout.endsWith("_left") || layout.endsWith("_right");
          const hasMedia = layout !== "text";
          const isFull = layout === "image" || layout === "video";
          return (
            <div key={i} className="bg-[#FFF9F0] nb-border rounded-xl p-3" data-testid={`content-row-${mat.key}-${i}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="font-head font-bold text-xs text-nbblack/50">Row #{i + 1}</span>
                <select value={layout} onChange={(e) => updRow(i, { layout: e.target.value })} className="px-2 py-1 rounded-lg nb-border bg-white font-bold text-xs" data-testid={`row-layout-${mat.key}-${i}`}>
                  {LAYOUTS.map((l) => <option key={l.v} value={l.v}>{l.label}</option>)}
                </select>
                {isFull && (
                  <select value={r.width || "wide"} onChange={(e) => updRow(i, { width: e.target.value })} className="px-2 py-1 rounded-lg nb-border bg-white font-bold text-xs" data-testid={`row-width-${mat.key}-${i}`}>
                    <option value="wide">Wide (full)</option>
                    <option value="normal">Centered</option>
                  </select>
                )}
                <div className="ml-auto flex gap-1">
                  <button onClick={() => move(i, -1)} className="p-1 nb-border rounded bg-white"><ArrowUp size={14} /></button>
                  <button onClick={() => move(i, 1)} className="p-1 nb-border rounded bg-white"><ArrowDown size={14} /></button>
                  <button onClick={() => delRow(i)} className="text-red-600 p-1"><Trash2 size={16} /></button>
                </div>
              </div>

              {hasMedia && (
                <div className="mb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full nb-border bg-white capitalize">{kind}</span>
                    {kind === "image" && (
                      <label className="inline-flex items-center gap-1 font-head font-bold px-3 py-1 rounded-full nb-border nb-press bg-white text-xs cursor-pointer">
                        {uploadingIdx === i ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload
                        <input type="file" accept="image/*" hidden onChange={(e) => upload(i, e.target.files?.[0])} />
                      </label>
                    )}
                  </div>
                  <input value={r.media_url || ""} onChange={(e) => updRow(i, { media_url: e.target.value })} placeholder={kind === "video" ? "YouTube / Vimeo / MP4 URL" : "Image URL (or upload above)"} className={inputCls} data-testid={`row-media-${mat.key}-${i}`} />
                  <input value={getL(r.caption, lang)} onChange={(e) => updRow(i, { caption: setL(r.caption, lang, e.target.value) })} placeholder="Caption (optional)" className={inputCls + " mt-2"} />
                  {kind === "image" && r.media_url && <img src={r.media_url.startsWith("http") ? r.media_url : `${process.env.REACT_APP_BACKEND_URL || ""}${r.media_url}`} alt="preview" className="mt-2 h-20 rounded-lg nb-border object-cover" />}
                </div>
              )}

              {hasText && (
                <textarea value={getL(r.text_md, lang)} onChange={(e) => updRow(i, { text_md: setL(r.text_md, lang, e.target.value) })} placeholder="Text (markdown: **bold**, [link](url), - lists)" className={areaCls} data-testid={`row-text-${mat.key}-${i}`} />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mt-3">
        <button onClick={() => addRow("text")} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-white text-xs" data-testid={`add-row-text-${mat.key}`}><Plus size={13} /> Text</button>
        <button onClick={() => addRow("image")} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-xs" data-testid={`add-row-image-${mat.key}`}><ImageIcon size={13} /> Image</button>
        <button onClick={() => addRow("image_left")} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-xs" data-testid={`add-row-imageleft-${mat.key}`}><ImageIcon size={13} /> Image + text</button>
        <button onClick={() => addRow("video")} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbyellow text-xs" data-testid={`add-row-video-${mat.key}`}><Video size={13} /> Video</button>
      </div>
    </div>
  );
}

// Editor for a FAQ list (question + markdown answer, reorderable).
function FaqEditor({ items, setItems, testid, lang }) {
  const upd = (i, patch) => setItems(items.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const del = (i) => setItems(items.filter((_, j) => j !== i));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
  };
  return (
    <div className="space-y-3">
      {items.map((f, i) => (
        <div key={i} className="bg-[#FFF9F0] nb-border rounded-xl p-3" data-testid={`${testid}-${i}`}>
          <div className="flex items-center gap-2 mb-2">
            <span className="font-head font-bold text-xs text-nbblack/50">#{i + 1}</span>
            <div className="ml-auto flex gap-1">
              <button onClick={() => move(i, -1)} className="p-1 nb-border rounded bg-white"><ArrowUp size={14} /></button>
              <button onClick={() => move(i, 1)} className="p-1 nb-border rounded bg-white"><ArrowDown size={14} /></button>
              <button onClick={() => del(i)} className="text-red-600 p-1"><Trash2 size={16} /></button>
            </div>
          </div>
          <input value={getL(f.q, lang)} onChange={(e) => upd(i, { q: setL(f.q, lang, e.target.value) })} placeholder="Question" className={inputCls} data-testid={`${testid}-q-${i}`} />
          <textarea value={getL(f.a_md, lang)} onChange={(e) => upd(i, { a_md: setL(f.a_md, lang, e.target.value) })} placeholder="Answer (markdown: **bold**, [link](url), - lists)" className={areaCls + " mt-2"} data-testid={`${testid}-a-${i}`} />
        </div>
      ))}
      <button onClick={() => setItems([...items, { q: { ...EMPTY_BI }, a_md: { ...EMPTY_BI } }])} className="inline-flex items-center gap-1 font-head font-bold px-3 py-1.5 rounded-full nb-border nb-press bg-nbcyan text-sm" data-testid={`${testid}-add`}>
        <Plus size={15} /> Add question
      </button>
    </div>
  );
}

export default function AdminContent({ content, setContent, materials }) {
  const [saving, setSaving] = useState(false);
  const [clang, setClang] = useState("en");

  const md = content.material_descriptions || {};
  const updMaterial = (key, desc) => setContent({ ...content, material_descriptions: { ...md, [key]: desc } });
  const setFaqLanding = (items) => setContent({ ...content, faq_landing: items });
  const setFaqDesigner = (items) => setContent({ ...content, faq_designer: items });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/content", content);
      setContent(data);
      toast.success("Content saved!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="sticky top-2 z-10 flex items-center gap-3 bg-white nb-border nb-shadow rounded-2xl p-3" data-testid="content-lang-toggle">
        <span className="font-head font-bold text-sm text-nbblack/60">Editing language:</span>
        {[{ v: "en", label: "English" }, { v: "fr", label: "Français" }].map((l) => (
          <button
            key={l.v}
            onClick={() => setClang(l.v)}
            data-testid={`content-lang-${l.v}`}
            className={`font-head font-bold text-sm px-4 py-1.5 rounded-full nb-border nb-press ${clang === l.v ? "bg-nbpink text-white" : "bg-white"}`}
          >
            {l.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-nbblack/50 hidden sm:block">French left blank falls back to English on the site.</span>
      </div>

      <Section title="Material descriptions" desc="Shown under the design canvas; swaps automatically with the selected material. Supports text, images and videos.">
        {materials.map((mat) => (
          <MaterialEditor
            key={mat.key}
            mat={mat}
            lang={clang}
            desc={md[mat.key] || { heading: { en: "", fr: "" }, rows: [] }}
            update={(desc) => updMaterial(mat.key, desc)}
          />
        ))}
      </Section>

      <Section title="Landing page FAQ" desc="Shown on the home page with FAQ rich-result markup for SEO.">
        <FaqEditor items={content.faq_landing || []} setItems={setFaqLanding} testid="faq-landing-edit" lang={clang} />
      </Section>

      <Section title="Designer page FAQ" desc="Shown on the designer page (single shared FAQ) with FAQ rich-result markup.">
        <FaqEditor items={content.faq_designer || []} setItems={setFaqDesigner} testid="faq-designer-edit" lang={clang} />
      </Section>

      <button
        data-testid="save-content"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 bg-nbblack text-white font-head font-bold text-lg px-8 py-3 rounded-full nb-border nb-shadow nb-press disabled:opacity-60"
      >
        {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />} Save content
      </button>
    </div>
  );
}
